import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import * as dayjs from 'dayjs';

export class DateValidator {
  static maxDate(value: Date, label = '', message = null): ValidatorFn {
    return (control: AbstractControl) => {
      if (control.invalid || !control.value) return null;
      return control.value > value ? { maxDate: { max: value, actual: control.value, label, message } } : null;
    };
  }

  static maxDateStr(value: Date, formatStr: string, label = '', message = null): ValidatorFn {
    return (control: AbstractControl) => {
      if (control.invalid || !control.value || typeof control.value !== 'string') return null;
      const controlValue = dayjs(control.value as string, formatStr);
      const maxDateValue = dayjs(value);
      const compareUnit = this.getStringDateCompareUnit(formatStr);

      if (compareUnit) {
        return controlValue.isAfter(maxDateValue, compareUnit)
          ? { maxDate: { max: value, actual: control.value, label, message } }
          : null;
      }

      return controlValue.toDate() > value ? { maxDate: { max: value, actual: control.value, label, message } } : null;
    };
  }

  static minDateStr(value: Date, formatStr: string, label = '', message = null): ValidatorFn {
    return (control: AbstractControl) => {
      if (control.invalid || !control.value || typeof control.value !== 'string') return null;
      const controlValue = dayjs(control.value as string, formatStr);
      const minDateValue = dayjs(value);
      const compareUnit = this.getStringDateCompareUnit(formatStr);

      if (compareUnit) {
        return controlValue.isBefore(minDateValue, compareUnit)
          ? { minDate: { min: value, actual: control.value, label, message } }
          : null;
      }

      return controlValue.toDate() < value ? { minDate: { min: value, actual: control.value, label, message } } : null;
    };
  }

  static minDate(value: Date, label = '', message = null): ValidatorFn {
    return (control: AbstractControl) => {
      if (control.invalid || !control.value) return null;
      return control.value < value ? { minDate: { min: value, actual: control.value, label, message } } : null;
    };
  }

  static maxYearValidator(value: number, label = ''): ValidatorFn {
    return (control: AbstractControl) => {
      if (control.invalid || !control.value) return null;
      return control.value > value ? { maxDate: { max: value, actual: control.value, label: label } } : null;
    };
  }

  static minYearValidator(value: number, label = ''): ValidatorFn {
    return (control: AbstractControl) => {
      if (control.invalid || !control.value) return null;
      return control.value < value ? { minDate: { min: value, actual: control.value, label: label } } : null;
    };
  }

  static isYearMonthFormat: ValidatorFn = (control: AbstractControl) => {
    return control.value === null ? { dateIsInvalid: true } : null;
  };

  static isYearMonthCurrent: ValidatorFn = (control: AbstractControl) => {
    if (control.invalid || !control.value) return null;
    const controlValue = control.value as Date;
    return controlValue.getMonth() <= new Date().getMonth() && controlValue.getFullYear() <= new Date().getFullYear()
      ? null
      : { dateIsNotCurrent: true };
  };

  static dateCompareValidator(compareControlName: string, type: 'minDate' | 'maxDate', label?: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || !control.parent) {
        return null;
      }
      const compareControl = control.parent.get(compareControlName);
      if (!compareControl?.value) {
        return null;
      }
      const value = new Date(control.value);
      const compareDate = new Date(compareControl.value);
      const isInvalid = type === 'minDate' ? value < compareDate : value > compareDate;
      if (!isInvalid) {
        return null;
      }
      return {
        [type]: {
          [type === 'minDate' ? 'min' : 'max']: compareDate,
          label,
          actual: value,
        },
      };
    };
  }

  private static getStringDateCompareUnit(formatStr: string): dayjs.OpUnitType | null {
    if (formatStr === 'YYYYMM') {
      return 'month';
    }

    return null;
  }
}
